import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { handleGenericError } from '../handle-generic-error';
import { displayToast } from '../app/app-slice';
import { getMessage } from '../translations/getMessage';

export const fetchShares = createAsyncThunk('share/fetch-shares', async ({ navigate }, thunkAPI) => {
  const zensignApiUrl = window.config.zensignApiUrl;
  return axios
    .get(`${zensignApiUrl}/share/shares`)
    .then((response) => {
      const allShares = response.data.data ?? [];

      const seenShareIds = new Set();
      const shares = allShares.filter(({ folderShareId }) => {
        if (seenShareIds.has(folderShareId)) return false;
        seenShareIds.add(folderShareId);
        return true;
      });

      shares.forEach(({ folderShareId, folderId, folder }) => {
        if (folder) {
          thunkAPI.dispatch(shareSlice.actions.fetchRootFolderMetadataSuccess({ folderShareId, rootFolder: folder }));
          thunkAPI.dispatch(fetchSubfolders({ folderShareId, folderId, navigate }));
        }
      });

      thunkAPI.dispatch(shareSlice.actions.fetchSharesSuccess({ ...response.data, data: shares }));
    })
    .catch((err) => {
      handleGenericError(thunkAPI.dispatch, err, navigate);
      throw thunkAPI.rejectWithValue(err.response?.data ?? {});
    });
});

export const fetchFolder = createAsyncThunk(
  'share/fetch-folder',
  async ({ folderShareId, folderId, navigate }, thunkAPI) => {
    const zensignApiUrl = window.config.zensignApiUrl;
    return axios
      .get(`${zensignApiUrl}/share/folders/${folderShareId}/folders/${folderId}`)
      .then((response) => {
        thunkAPI.dispatch(shareSlice.actions.fetchFolderSuccess({ folderId, data: response.data }));
      })
      .catch((err) => {
        const status = err.response?.status;

        if ([403, 404, 410].includes(status)) {
          thunkAPI.dispatch(shareSlice.actions.markFolderInaccessible({ folderId }));
        } else {
          handleGenericError(thunkAPI.dispatch, err, navigate);
        }

        throw thunkAPI.rejectWithValue({ folderId, status });
      });
  }
);

export const fetchSubfolders = createAsyncThunk(
  'share/fetch-subfolders',
  async ({ folderShareId, folderId, navigate }, thunkAPI) => {
    const zensignApiUrl = window.config.zensignApiUrl;

    await axios
      .get(`${zensignApiUrl}/share/folders/${folderShareId}/folders`, { params: { parentId: folderId } })
      .then((response) => {
        const children = response.data?.folders ?? [];
        thunkAPI.dispatch(shareSlice.actions.fetchSubfoldersSuccess({ folderId, children }));
      })
      .catch((err) => {
        const status = err.response?.status;
        const locale = thunkAPI.getState().app?.locale || 'en-US';

        if ([401, 500].includes(status)) {
          handleGenericError(thunkAPI.dispatch, err, navigate);
        } else if ([403, 404, 410].includes(status)) {
          thunkAPI.dispatch(shareSlice.actions.markFolderInaccessible({ folderId }));
          thunkAPI.dispatch(displayToast(getMessage(locale, 'errors.itemNotFound')));
        } else {
          thunkAPI.dispatch(displayToast(getMessage(locale, 'errors.somethingWentWrong')));
        }

        thunkAPI.dispatch(shareSlice.actions.fetchSubfoldersFailure({ folderId }));
        throw thunkAPI.rejectWithValue({ folderId });
      });
  }
);

const shareSlice = createSlice({
  name: 'share',
  initialState: {
    shares: [],
    folders: {},
    folderRootMetadata: {},
    isLoading: false,
    hasLoaded: false,
    loadFailed: false,
    subfolders: {},
    expandedNodeIds: [],
    loadingNodeIds: [],
    subfoldersError: {},
    inaccessibleFolderIds: [],
  },
  reducers: {
    fetchSharesSuccess: (state, action) => {
      state.shares = action.payload.data;
      state.isLoading = false;
      state.hasLoaded = true;
      state.loadFailed = false;
    },
    fetchFolderSuccess: (state, action) => {
      state.folders[action.payload.folderId] = action.payload.data;
    },
    fetchRootFolderMetadataSuccess: (state, action) => {
      const { folderShareId, rootFolder } = action.payload;
      if (rootFolder) {
        state.folderRootMetadata[folderShareId] = rootFolder;
      }
    },
    fetchSubfoldersFailure: (state, action) => {
      const { folderId } = action.payload;
      return {
        ...state,
        loadingNodeIds: state.loadingNodeIds.filter((id) => id !== folderId),
        subfoldersError: { ...state.subfoldersError, [folderId]: true },
      };
    },
    setExpandedNodeIds: (state, action) => {
      return { ...state, expandedNodeIds: action.payload };
    },
    markFolderInaccessible: (state, action) => {
      const { folderId } = action.payload;
      if (state.inaccessibleFolderIds.includes(folderId)) return state;
      return { ...state, inaccessibleFolderIds: [...state.inaccessibleFolderIds, folderId] };
    },
    fetchSubfoldersSuccess: (state, action) => {
      const { folderId, children } = action.payload;
      return {
        ...state,
        subfolders: { ...state.subfolders, [folderId]: children },
        loadingNodeIds: state.loadingNodeIds.filter((id) => id !== folderId),
        subfoldersError: { ...state.subfoldersError, [folderId]: undefined },
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchShares.pending, (state) => {
        state.isLoading = true;
        state.loadFailed = false;
      })
      .addCase(fetchShares.rejected, (state) => {
        return { ...state, isLoading: false, loadFailed: true };
      })
      .addCase(fetchSubfolders.pending, (state, action) => {
        const { folderId } = action.meta.arg;
        if (state.loadingNodeIds.includes(folderId)) return state;
        return { ...state, loadingNodeIds: [...state.loadingNodeIds, folderId] };
      });
  },
});

export default shareSlice;
